import React, { useState, useEffect } from 'react';
import { Plus, X, Search } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  GetAllSystemsAction, 
  AddSystemAction, 
  GetSystemByNameAction 
} from "../../actions/SystemAction";

// Import your components
import SystemTable from '../../components/tables/SystemTable';
import AddSystemModal from '../../components/modals/AddSystemModal';
import SystemPagination from '../../components/common/SystemPagination'; // Updated import

// Import selector functions
import {
  selectAllSystemsData,
  selectAllSystemsLoading,
  selectAllSystemsError,
  selectAddSystemData,
  selectAddSystemLoading,
  selectAddSystemError,
  selectSystemByNameData,
  selectSystemByNameLoading,
  selectSystemByNameError
} from "../../reducers/SystemReducer";

const SystemManagement = () => {
  const dispatch = useDispatch();

  // Use selector functions instead of direct state paths
  const systemsData = useSelector(selectAllSystemsData);
  const systemsLoading = useSelector(selectAllSystemsLoading);
  const systemsError = useSelector(selectAllSystemsError);

  const addSystemResult = useSelector(selectAddSystemData);
  const addSystemLoading = useSelector(selectAddSystemLoading);
  const addSystemError = useSelector(selectAddSystemError);

  const searchResult = useSelector(selectSystemByNameData);
  const searchLoading = useSelector(selectSystemByNameLoading);
  const searchError = useSelector(selectSystemByNameError);

  const [systems, setSystems] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // You can make this configurable if needed

  const [newSystem, setNewSystem] = useState({
    system_name: '',
    description: ''
  });

  useEffect(() => {
    loadSystems();
  }, []);

  useEffect(() => {
    if (systemsData) {
      formatAndSetSystems(systemsData);
    }
  }, [systemsData]);

  useEffect(() => {
    if (searchResult && !searchLoading) {
      formatAndSetSystems(searchResult);
    }
  }, [searchResult, searchLoading]);

  useEffect(() => {
    if (addSystemResult && !addSystemLoading) {
      console.log('New system added successfully, reloading systems...');
      loadSystems();
      setShowAddForm(false);
      setNewSystem({ system_name: "", description: "" });
    }
  }, [addSystemResult, addSystemLoading]);

  // Reset to page 1 when search results change
  useEffect(() => {
    setCurrentPage(1);
  }, [systems]);

  const formatAndSetSystems = (data) => {
    let systemsArray = [];

    if (Array.isArray(data)) {
      systemsArray = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.ResultSet)) systemsArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) systemsArray = data.resultSet;
      else if (Array.isArray(data.data)) systemsArray = data.data;
      else if (Array.isArray(data.responseBody)) systemsArray = data.responseBody;
      else if (Array.isArray(data.systems)) systemsArray = data.systems;
      else if (data.system_id) systemsArray = [data];
    }

    const formatted = systemsArray.map((system, index) => ({
      system_id: system.system_id || system.SystemID || `temp-${index}`,
      system_name: system.system_name || system.SystemName || 'Unnamed',
      description: system.description || system.Description || 'No description'
    }));

    setSystems(formatted);
  };

  const loadSystems = async () => {
    try {
      dispatch(GetAllSystemsAction());
    } catch (error) {
      console.error('Error loading systems:', error);
    }
  };

  const handleAddSystem = async (e) => {
    e.preventDefault();

    if (!newSystem.system_name.trim()) {
      alert('System name is required');
      return;
    }

    try {
      dispatch(AddSystemAction(newSystem));
    } catch (error) {
      console.error('Error adding system:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchTerm.trim()) {
      await loadSystems();
      return;
    }

    try {
      dispatch(GetSystemByNameAction(searchTerm));
    } catch (error) {
      console.error('Error searching systems:', error);
    }
  };

  const handleInputChange = (e) => {
    setNewSystem(prev => ({ 
      ...prev, 
      [e.target.name]: e.target.value 
    }));
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    loadSystems();
  };

  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setNewSystem({ system_name: "", description: "" });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Calculate paginated data
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSystems = systems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(systems.length / itemsPerPage);

  return (
    <div className="h-full flex flex-col p-4 lg:p-6">
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">System Management</h1>
        <p className="text-sm md:text-base text-gray-600">Manage your software systems and licenses</p>
      </div>

      {(systemsError || addSystemError || searchError) && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm md:text-base">
          <strong>Error:</strong> {systemsError || addSystemError || searchError}
        </div>
      )}

      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <form onSubmit={handleSearch} className="flex-1 max-w-full lg:max-w-md relative">
          <input
            type="text"
            placeholder="Search systems by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={systemsLoading || searchLoading}
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          {searchTerm && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              disabled={systemsLoading || searchLoading}
            >
              <X size={18} />
            </button>
          )}
        </form>

        <button
          onClick={() => setShowAddForm(true)}
          disabled={addSystemLoading || systemsLoading}
          className="bg-blue-500 text-white px-4 md:px-6 py-2 rounded-lg flex items-center justify-center space-x-2 lg:ml-4 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed w-full lg:w-auto"
        >
          <Plus size={18} />
          <span className="text-sm md:text-base">{addSystemLoading ? 'Adding...' : 'Add New System'}</span>
        </button>
      </div>

      {/* Use AddSystemModal component */}
      <AddSystemModal
        isOpen={showAddForm}
        onClose={handleCloseAddForm}
        onSubmit={handleAddSystem}
        newSystem={newSystem}
        onChange={handleInputChange}
        isLoading={addSystemLoading}
        error={addSystemError}
      />

      {(systemsLoading || searchLoading) && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600 text-sm md:text-base">
            {searchLoading ? 'Searching systems...' : 'Loading systems...'}
          </p>
        </div>
      )}

      {/* System Table Container */}
      {!systemsLoading && !searchLoading && (
        <div className="flex-1 min-h-0 overflow-x-auto">
          <div className="bg-white rounded-lg shadow overflow-hidden h-full flex flex-col min-w-[768px]">
            {/* Use SystemTable component with paginated data */}
            <SystemTable
              systems={currentSystems}
              searchTerm={searchTerm}
            />
            
            {/* SystemPagination component */}
            {systems.length > 0 && (
              <div className="flex-shrink-0">
                <SystemPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={systems.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  disabled={systemsLoading || searchLoading}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemManagement;